import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { Scene } from "@shared/schema";
import { SCENE_STATUS_LABEL, sceneBadgeClass } from "../../lib/format";

export default function SceneCard({ scene, projectId }: { scene: Scene; projectId: string }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["project", projectId] });
  const [tab, setTab] = useState<"frame" | "video">("frame");
  const [openPrompts, setOpenPrompts] = useState(false);
  const [framePrompt, setFramePrompt] = useState(scene.imagePrompt ?? "");
  const [videoPrompt, setVideoPrompt] = useState(scene.videoPrompt ?? "");

  const isApp = scene.kind === "figma_ui";

  const frame = scene.frame?.versions.find((v) => v.id === scene.frame?.currentId) ?? scene.frame?.versions.at(-1);
  const video = scene.video?.versions.find((v) => v.id === scene.video?.currentId) ?? scene.video?.versions.at(-1);

  const genFrame = useMutation({ mutationFn: () => api.generateFrame(scene.id), onSuccess: refresh });
  const genVideo = useMutation({ mutationFn: () => api.generateVideo(scene.id), onSuccess: refresh });
  const save = useMutation({
    mutationFn: () => api.updateScene(scene.id, { imagePrompt: framePrompt, videoPrompt }),
    onSuccess: refresh,
  });

  const busy = genFrame.isPending || genVideo.isPending;

  return (
    <div className="card" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--line-2)" }}>
        <span className="badge">#{scene.idx + 1}</span>
        <span className="badge blue">{secOrTc(scene)}</span>
        <span style={{ flex: 1 }} />
        <span className={"badge " + (isApp ? "amber" : "blue")}>{isApp ? "🚫 App-UI" : "AI"}</span>
      </div>

      {/* media 9:16 */}
      <div style={{ position: "relative", aspectRatio: "9 / 16", background: "linear-gradient(160deg,#eef2fb,#e3e9f7)", display: "grid", placeItems: "center" }}>
        {isApp ? (
          <div style={{ padding: 14, fontSize: 12, color: "var(--muted)" }}>
            <strong>Layout spec (Figma/AE)</strong>
            <div className="hint" style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{scene.layoutSpec ?? "—"}</div>
          </div>
        ) : tab === "video" && video?.url ? (
          <video src={video.url} controls muted loop style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : frame?.url ? (
          <img src={frame.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ color: "var(--muted-2)" }}>{scene.status === "frame_queued" ? <span className="spinner" /> : "9:16"}</span>
        )}

        {!isApp && (
          <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 4 }}>
            <button className={"btn sm " + (tab === "frame" ? "primary" : "")} onClick={() => setTab("frame")}>Кадр</button>
            <button className={"btn sm " + (tab === "video" ? "primary" : "")} onClick={() => setTab("video")} disabled={!video}>Відео</button>
          </div>
        )}
      </div>

      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <span className={"badge dot " + sceneBadgeClass(scene.status)} style={{ alignSelf: "flex-start" }}>
          {SCENE_STATUS_LABEL[scene.status]}
        </span>
        <div style={{ fontWeight: 700 }}>{scene.title || "Без назви"}</div>
        {scene.task && <div className="hint">{scene.task}</div>}

        {!isApp && (
          <>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn sm" style={{ flex: 1 }} disabled={busy} onClick={() => genFrame.mutate()}>
                {genFrame.isPending ? <span className="spinner" /> : frame ? "↻ Кадр" : "Згенерувати кадр"}
              </button>
              <button className="btn sm" style={{ flex: 1 }} disabled={busy || !frame} onClick={() => genVideo.mutate()}>
                {genVideo.isPending ? <span className="spinner" /> : video ? "↻ Відео" : "Кадр → відео"}
              </button>
            </div>
            <div className="hint" style={{ cursor: "pointer", color: "var(--blue-700)" }} onClick={() => setOpenPrompts((o) => !o)}>
              {openPrompts ? "▾ Промпти" : "▸ Промпти"}
            </div>
            {openPrompts && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div className="field" style={{ margin: 0 }}>
                  <label>First-frame (Flux/Mystic)</label>
                  <textarea className="textarea" value={framePrompt} onChange={(e) => setFramePrompt(e.target.value)} />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>Video (Kling)</label>
                  <textarea className="textarea" value={videoPrompt} onChange={(e) => setVideoPrompt(e.target.value)} />
                </div>
                <button className="btn sm" onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? <span className="spinner" /> : "Зберегти промпти"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function secOrTc(scene: Scene): string {
  if (scene.startS != null && scene.endS != null) {
    return `${fmt(scene.startS)}–${fmt(scene.endS)}`;
  }
  return `${scene.durationS}s`;
}
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
}
